import {
  BarChart3,
  Box,
  FileText,
  LayoutDashboard,
  Menu,
  RotateCcw,
  Settings,
  Settings2,
  X,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Lock,
  Activity,
  Terminal,
  ShoppingBag,
  CreditCard,
  Users,
  User,
  AlertCircle,
  CheckCircle,
  Loader2,
  Shield,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';

import { useAuth } from '../context/AuthContext.js';
import { api } from '@/lib/api';

const navigation = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard },
  { name: 'Inventory', href: '/inventory', icon: Box },
  { name: 'POS Terminal', href: '/billing?mode=terminal', icon: ShoppingBag },
  { name: 'Customers', href: '/customers', icon: Users },
  { name: 'Billing Logs', href: '/billing', icon: FileText },
  { name: 'Returns', href: '/returns', icon: RotateCcw },
  { name: 'Reports', href: '/reports', icon: BarChart3 },
  { name: 'Company Settings', href: '/settings', icon: Settings2 },
  { name: 'My Profile', href: '/profile', icon: User },
  { name: 'SaaS Overview', href: '/saas-admin', icon: Settings },
  { name: 'Subscription Plans', href: '/saas-admin/plans', icon: CreditCard },
  { name: 'Platform Telemetry', href: '/saas-admin/telemetry', icon: Activity },
  { name: 'Audit Trail', href: '/saas-admin/audits', icon: Terminal },
];

export function DashboardLayout() {
  const { user, logout, acceptTerms } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [agreedCheck, setAgreedCheck] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState('');

  useEffect(() => {
    if (user && user.role !== 'SUPER_ADMIN' && !user.agreedToTerms) {
      setShowTermsModal(true);
    } else {
      setShowTermsModal(false);
    }
  }, [user]);

  const handleAcceptTerms = async () => {
    if (!agreedCheck) return;
    setAccepting(true);
    setAcceptError('');
    try {
      const response = await api.post('/tenant/users/agree-terms', {}, {
        headers: { 'x-tenant-id': user?.tenantId }
      });
      if (response.data.success) {
        acceptTerms();
        setShowTermsModal(false);
      }
    } catch (err: any) {
      setAcceptError(err.response?.data?.message || 'Failed to accept policies.');
    } finally {
      setAccepting(false);
    }
  };
  const location = useLocation();

  const tenantFlags = JSON.parse(
    localStorage.getItem(`tenant_flags_${user?.tenantId}`) ||
    '{"bulkMatrix":false,"gstComplianceExport":false,"loyaltyEngine":false}',
  );

  const isReportsGated = false;

  const filteredNavigation = navigation.filter((item) => {
    if (user?.role === 'SUPER_ADMIN') {
      return (
        item.name === 'SaaS Overview' ||
        item.name === 'Subscription Plans' ||
        item.name === 'Platform Telemetry' ||
        item.name === 'Audit Trail' ||
        item.name === 'My Profile'
      );
    }
    if (item.name === 'Customers' && !tenantFlags.loyaltyEngine) {
      return false;
    }
    return (
      item.name !== 'SaaS Overview' &&
      item.name !== 'Subscription Plans' &&
      item.name !== 'Platform Telemetry' &&
      item.name !== 'Audit Trail'
    );
  });

  return (
    <div className="flex h-screen overflow-hidden bg-surface-50">
      {/* ─── Mobile Overlay ────────────────────────────────── */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
          role="button"
          tabIndex={0}
          aria-label="Close sidebar"
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setSidebarOpen(false);
            }
          }}
        />
      )}

      {/* ─── Sidebar ───────────────────────────────────────── */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-50 relative
          transform bg-surface-900 text-white
          transition-all duration-300 ease-in-out
          lg:static lg:translate-x-0 flex flex-col justify-between shrink-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          ${sidebarCollapsed ? 'lg:w-20' : 'lg:w-64'}
          w-64
        `}
      >
        {/* Desktop Collapse/Expand Floating Button */}
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="hidden lg:flex absolute top-6 -right-3 h-6 w-6 items-center justify-center rounded-full border border-surface-700 bg-surface-900 text-surface-400 hover:text-white hover:bg-surface-800 shadow-md cursor-pointer transition-all duration-300 z-50"
          title={sidebarCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
          aria-label={sidebarCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
        >
          {sidebarCollapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
        </button>

        <div>
          {/* Logo */}
          <div className="flex h-16 items-center justify-between px-6 border-b border-surface-700">
            <h1 className={`text-lg font-bold tracking-tight transition-all duration-300 ${sidebarCollapsed ? 'lg:opacity-0 lg:w-0 overflow-hidden' : 'opacity-100'}`}>
              <span className="text-primary-400">Tria</span>POS
            </h1>
            {sidebarCollapsed && (
              <span className="hidden lg:block text-primary-400 font-extrabold text-sm mx-auto">
                T-P
              </span>
            )}
            <button
              className="lg:hidden text-surface-400 hover:text-white"
              onClick={() => setSidebarOpen(false)}
              aria-label="Close sidebar"
            >
              <X size={20} />
            </button>
          </div>

          {/* Navigation */}
          <nav className="mt-6 px-3 space-y-1">
            {filteredNavigation.map((item) => {
              const isLocked = false;

              const itemPath = item.href.split('?')[0];
              const itemMode = new URLSearchParams(item.href.split('?')[1] || '').get('mode');
              const currentParams = new URLSearchParams(location.search);
              const currentMode = currentParams.get('mode');
              
              let isLinkActive = false;
              if (itemPath === '/') {
                isLinkActive = location.pathname === '/';
              } else if (itemPath === '/billing') {
                if (itemMode === 'terminal') {
                  isLinkActive = location.pathname === '/billing' && currentMode === 'terminal';
                } else {
                  isLinkActive = location.pathname === '/billing' && currentMode !== 'terminal';
                }
              } else {
                isLinkActive = location.pathname === itemPath;
              }

              return (
                <NavLink
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  title={
                    sidebarCollapsed
                      ? isLocked
                        ? `${item.name} (Premium Locked)`
                        : item.name
                      : undefined
                  }
                  className={() =>
                    `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium
                     transition-all duration-150 relative group
                     ${sidebarCollapsed ? 'lg:justify-center lg:px-0' : ''}
                     ${isLinkActive
                      ? 'bg-primary-600/20 text-primary-400'
                      : 'text-surface-300 hover:bg-surface-800 hover:text-white'
                     }`
                  }
                >
                  <item.icon size={18} className="shrink-0" />
                  <span
                    className={`transition-all duration-300 flex items-center justify-between flex-1 ${sidebarCollapsed ? 'lg:hidden' : 'inline'}`}
                  >
                    <span>{item.name}</span>
                    {isLocked && <Lock size={12} className="text-amber-500 shrink-0 ml-1.5" />}
                  </span>
                  {isLocked && sidebarCollapsed && (
                    <div className="absolute right-2 top-2 w-2 h-2 rounded-full bg-amber-500 border border-surface-900 hidden lg:block group-hover:scale-125 transition-transform" />
                  )}
                </NavLink>
              );
            })}
          </nav>
        </div>

        {/* Sidebar Footer Logout Option */}
        <div className="p-4 border-t border-surface-800">
          <button
            onClick={() => logout()}
            title={sidebarCollapsed ? 'Log Out' : undefined}
            className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-rose-450 hover:bg-rose-500/10 hover:text-rose-400 transition-colors duration-150 cursor-pointer ${sidebarCollapsed ? 'lg:justify-center lg:px-0' : ''
              }`}
          >
            <LogOut size={18} className="shrink-0" />
            <span
              className={`transition-all duration-300 ${sidebarCollapsed ? 'lg:hidden' : 'inline'}`}
            >
              Log Out
            </span>
          </button>
        </div>
      </aside>

      {/* ─── Main Content ──────────────────────────────────── */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Top Bar */}
        <header className="flex h-16 items-center gap-4 border-b border-surface-200 bg-white px-6 shadow-sm">
          {/* Mobile hamburger menu toggle */}
          <button
            className="text-surface-500 hover:text-surface-700 lg:hidden"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open sidebar"
          >
            <Menu size={22} />
          </button>

          <div className="flex-1" />
          <div className="flex items-center gap-3.5">
            <NavLink to="/profile" className="flex items-center gap-3 animate-fadeIn hover:opacity-85 transition-all cursor-pointer">
              <div className="hidden md:flex flex-col items-end text-xs">
                <span className="font-bold text-slate-800">
                  {user?.firstName} {user?.lastName}
                </span>
                <span className="text-[10px] text-slate-400 font-medium">
                  {user?.role === 'SUPER_ADMIN' ? 'SaaS Super Admin' : user?.tenantName}
                </span>
              </div>
              <div className="h-9 w-9 rounded-full bg-primary-600 flex items-center justify-center text-white text-xs font-extrabold border border-primary-500">
                {user?.firstName?.slice(0, 2).toUpperCase() || 'US'}
              </div>
            </NavLink>
            <button
              onClick={() => logout()}
              className="px-3 py-1.5 border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer shadow-sm"
              title="Log Out"
            >
              <LogOut size={14} className="text-slate-500" />
              <span>Log Out</span>
            </button>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-6 bg-slate-50/50">
          {isReportsGated ? (
            <div className="flex flex-col items-center justify-center h-full max-w-xl mx-auto text-center space-y-6 py-12">
              <div className="w-16 h-16 rounded-2xl bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-500 shadow-md">
                <Lock size={32} className="animate-bounce text-amber-550" />
              </div>
              <div className="space-y-2">
                <span className="text-[10px] text-amber-600 font-bold uppercase tracking-wider bg-amber-50 border border-amber-200/50 px-2.5 py-1 rounded-full">
                  Premium Module Locked
                </span>
                <h2 className="text-2xl font-extrabold text-slate-800 tracking-tight">
                  Unlock GST Audits & Financial Hub
                </h2>
                <p className="text-sm text-slate-500 leading-relaxed">
                  Your current subscription (Silver/Gold) does not include the advanced GST ledger
                  serialization or time-series brand revenue diagnostics.
                </p>
              </div>
              <div className="w-full bg-white border border-slate-150 p-5 rounded-2xl text-left text-xs space-y-3 shadow-sm">
                <span className="font-bold text-slate-700 block border-b border-slate-100 pb-2">
                  Unlocked on Platinum:
                </span>
                <ul className="space-y-2.5 text-slate-500 font-semibold">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    <span>
                      Generate CGST, SGST, and IGST breakdowns for direct financial audits.
                    </span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    <span>Download automated compliance-ready GST CSV ledger records.</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    <span>
                      Inspect time-series performance metrics for specific apparel brands.
                    </span>
                  </li>
                </ul>
              </div>
              <p className="text-xs text-slate-400 font-medium">
                Please contact the platform system administrator at{' '}
                <span className="font-bold text-emerald-600">support@threads.com</span> to instantly
                upgrade your license to Platinum.
              </p>
            </div>
          ) : (
            <Outlet />
          )}
        </main>
      </div>

      {/* Global Terms & Conditions Blocking Modal Overlay */}
      {showTermsModal && (
        <div className="fixed inset-0 z-[9999] bg-slate-900/80 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden border border-slate-100 flex flex-col max-h-[90vh] animate-scaleUp">
            
            {/* Header */}
            <div className="p-6 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
              <div>
                <h2 className="text-base font-extrabold text-slate-950 tracking-tight">TRIA POS Application Policies</h2>
                <span className="text-[10px] font-mono text-slate-400 font-bold uppercase tracking-wider block mt-0.5">
                  KATIRTEK TRIA PVT LTD
                </span>
              </div>
              <span className="px-2.5 py-1 bg-primary-50 border border-primary-200 text-[10px] font-bold text-primary-700 rounded-full">
                Action Required
              </span>
            </div>

            {/* Scrollable Policy Contents */}
            <div className="p-6 overflow-y-auto space-y-5 text-xs text-slate-655 leading-relaxed font-medium divide-y divide-slate-100">
              
              <div className="space-y-2.5">
                <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-1.5">
                  <FileText className="text-primary-600" size={14} />
                  <span>1. Terms & Conditions</span>
                </h3>
                <p>
                  Welcome to **TRIA POS**. These terms govern your use of the POS billing terminals, inventory logs, and accounting dashboards. TRIA POS is developed, owned, and serviced by **KATIRTEK TRIA PVT LTD**.
                </p>
                <p>
                  By checking the agreement box and continuing, you grant approval on behalf of your tenant store organization to utilize these SaaS services under the parameters and user caps defined in your active licensing plan tier.
                </p>
              </div>

              <div className="space-y-2.5 pt-4">
                <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-1.5">
                  <Shield className="text-primary-600" size={14} />
                  <span>2. Privacy & Data Governance</span>
                </h3>
                <p>
                  We store billing records, sales totals, customer metadata, and inventory details inside securely isolated databases using the specific workspace tenant identity. Your store statistics are kept confidential and are not shared with any third-party organizations.
                </p>
              </div>

              <div className="space-y-2.5 pt-4">
                <h3 className="text-xs font-bold text-rose-800 uppercase tracking-wider flex items-center gap-1.5">
                  <AlertCircle className="text-rose-600 animate-pulse" size={14} />
                  <span>3. Refund Policy</span>
                </h3>
                <div className="p-3 bg-rose-50 border border-rose-150 rounded-lg text-rose-800 font-bold">
                  Refund Policy: Once paid, there is no refund for the customer.
                </div>
                <p className="text-slate-500">
                  This refund rule applies to all store billing cycles, license fees, transactions, invoice payments, and return vouchers registered within TRIA POS. Settlements are final and non-refundable.
                </p>
              </div>

              <div className="space-y-2 pt-4 text-center">
                <p className="text-slate-400">
                  For any support queries or account updates, please raise a mail to: 
                  <a href="mailto:support@katirtek.com" className="font-bold text-primary-600 hover:underline ml-1">support@katirtek.com</a>
                </p>
              </div>

            </div>

            {/* Error alerts */}
            {acceptError && (
              <div className="mx-6 px-4 py-2 bg-rose-50 border border-rose-100 text-[10px] text-rose-700 font-bold rounded-lg flex items-center gap-2">
                <AlertCircle size={12} className="shrink-0 text-rose-600" />
                <span>{acceptError}</span>
              </div>
            )}

            {/* Footer buttons & Agree check */}
            <div className="p-6 border-t border-slate-100 bg-slate-50 space-y-4">
              <div className="flex items-start gap-2.5">
                <input
                  type="checkbox"
                  id="agree-checkbox"
                  checked={agreedCheck}
                  onChange={(e) => setAgreedCheck(e.target.checked)}
                  className="w-4 h-4 text-primary-650 border-slate-350 rounded focus:ring-primary-500 cursor-pointer mt-0.5"
                />
                <label htmlFor="agree-checkbox" className="text-xs font-semibold text-slate-700 cursor-pointer select-none">
                  I agree to the Terms of Service, Privacy Policy, and Refund Policy of TRIA POS. I confirm that all financial transactions are governed by the strict no-refund policy.
                </label>
              </div>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => logout()}
                  className="px-4 py-2 border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-xs font-semibold rounded-lg shadow-sm cursor-pointer"
                >
                  Log Out / Exit
                </button>
                <button
                  type="button"
                  onClick={handleAcceptTerms}
                  disabled={!agreedCheck || accepting}
                  className="flex-1 py-2 bg-primary-600 hover:bg-primary-700 active:bg-primary-800 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-bold rounded-lg shadow border border-primary-500 flex items-center justify-center gap-1.5 cursor-pointer transition-all"
                >
                  {accepting ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      <span>Recording Consent...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle size={14} />
                      <span>Agree & Proceed</span>
                    </>
                  )}
                </button>
              </div>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}
